import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { publicApi } from "../lib/api";
import { useContent, renderHighlightedTitle } from "../lib/useContent";

const CATEGORY_FALLBACK_IMAGE = {
  commercio: "https://images.unsplash.com/photo-1481437156560-3205f6a55735?w=1200&q=80&auto=format&fit=crop",
  artigianato: "https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=1200&q=80&auto=format&fit=crop",
  turismo: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80&auto=format&fit=crop",
  servizi: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80&auto=format&fit=crop",
  "enti-associazioni": "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1200&q=80&auto=format&fit=crop",
};

const FALLBACK = [
  { slug: "commercio", name: "Commercio", description: "Negozi di quartiere, boutique e insegne storiche.", image: CATEGORY_FALLBACK_IMAGE.commercio },
  { slug: "artigianato", name: "Artigianato", description: "Botteghe e mestieri tradizionali di Sanluri.", image: CATEGORY_FALLBACK_IMAGE.artigianato },
  { slug: "turismo", name: "Turismo", description: "Servizi e agenzie del territorio.", image: CATEGORY_FALLBACK_IMAGE.turismo },
  { slug: "servizi", name: "Servizi", description: "Professionisti e servizi utili.", image: CATEGORY_FALLBACK_IMAGE.servizi },
  { slug: "enti-associazioni", name: "Enti Pubblici o Privati e Associazioni", description: "Cultura, sport, comunità e istituzioni.", image: CATEGORY_FALLBACK_IMAGE["enti-associazioni"] },
];

const CategoryGrid = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { field } = useContent("home");

  const pretitle = field("category_grid", "pretitle", "Esplora le categorie");
  const headline = field("category_grid", "headline", "Un ecosistema di cinque anime.");
  const highlightTerms = field("category_grid", "highlight_terms", ["cinque anime"]);
  const description = field("category_grid", "description", "Dal commercio all'artigianato, dal turismo ai servizi ed enti: ogni categoria racconta un pezzo di Sanluri.");

  useEffect(() => {
    let cancelled = false;
    publicApi.listCategories()
      .then((r) => { if (!cancelled) setCats((r.data || []).filter((x) => x.published !== false)); })
      .catch(() => { if (!cancelled) setCats([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const items = (cats.length ? cats : FALLBACK).slice(0, 8);

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            {pretitle !== "" && (
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#12964A]" /> {pretitle}
              </div>
            )}
            <h2 className="font-display font-bold text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#1F2937] max-w-3xl">
              {renderHighlightedTitle(headline, highlightTerms)}
            </h2>
          </div>
          {description !== "" && (
            <p className="text-slate-600 max-w-md text-lg">{description}</p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={`cat-skeleton-${i}`} className="aspect-[4/5] rounded-3xl skeleton" />
              ))
            : items.map((cat, i) => (
                <Link key={cat.slug} to={`/categoria/${cat.slug}`} className="group relative overflow-hidden rounded-3xl bg-[#F0FAF3] card-shadow">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={cat.cover_image_url || CATEGORY_FALLBACK_IMAGE[cat.slug] || cat.image || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80"}
                      alt={cat.name} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />
                    <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-white">
                      <span className="text-xs uppercase tracking-[0.2em] opacity-90">0{i + 1}</span>
                      <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <ArrowUpRight className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <div className="absolute inset-x-5 bottom-5 text-white">
                      <h3 className="font-display font-bold text-xl md:text-2xl mb-1 leading-tight">{cat.name}</h3>
                      {cat.description && <p className="text-xs md:text-sm text-white/85 leading-snug line-clamp-2">{cat.description}</p>}
                    </div>
                  </div>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryGrid;
